import { memo } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { Text, HStack, VStack, Heading, ErrorAlertBanner } from '@library';
import type {
    ProductCustomerPrice,
    ProductItem,
    ProductWithRelatedData,
} from '../../../types';
import ProductTileAvailability from '../../ProductTileView/ProductTileAvailability';
import ProductTileTag from '../../ProductTileView/ProductTileTag';
import ProductTileCarousel from '../../ProductTileView/ProductTileCarousel';
import useProductView from '../../../hooks/useProductView';
import withErrorBoundary from '../../../../core/components/errors/withErrorBoundary';
import CurrentStoreLink from '../../../../core/components/router/CurrentStoreLink';
import ProductTilePricing from '../../ProductTileView/ProductTilePricing';
import ProductTileActions from '../../ProductTileView/ProductTileActions';
import styles from './ProductTableView.module.scss';

const ProductImageContent = memo(
    ({
        product,
        productDetailsLink,
    }: {
        product: ProductItem;
        productDetailsLink: string;
    }) => {
        return (
            <div className={styles.productImageContainer}>
                <div className={styles.productTag}>
                    <ProductTileTag product={product} />
                </div>
                <div className={styles.productImageCarousel}>
                    <ProductTileCarousel
                        overviewPlacement="top"
                        productDetailsLink={productDetailsLink}
                        media={product.media.slice(0, 1)}
                    />
                </div>
            </div>
        );
    }
);

interface ProductTableViewProps {
    product: ProductWithRelatedData;
    renderInstantly?: boolean;
    loading?: boolean;
}

const ProductTablePrice = memo(
    ({
        product,
        selectedSKU: activeSKU,
        customerPrice,
        variantStartingPrice,
    }: {
        product: ProductWithRelatedData;
        selectedSKU?: string;
        customerPrice?: ProductCustomerPrice;
        variantStartingPrice?: boolean;
    }) => {
        return (
            <ProductTilePricing
                gap="none"
                variantStartingPrice={variantStartingPrice}
                customerPrice={customerPrice}
                product={product}
                selectedSKU={activeSKU}
                divider={false}
            />
        );
    }
);

const ProductDetails = memo(
    ({
        product,
        productDetailsLink,
        activeSKU,
    }: {
        product: ProductWithRelatedData;
        productDetailsLink: string;
        activeSKU?: string;
    }) => {
        return (
            <VStack gap="micro">
                <HStack>
                    <Text medium secondary>
                        <FormattedMessage id="Common ItemNumber" />
                        :&nbsp;
                    </Text>
                    <Text uppercase secondary>
                        {activeSKU || (
                            <FormattedMessage id="please make a choice" />
                        )}
                    </Text>
                </HStack>

                <div>
                    <CurrentStoreLink
                        to={productDetailsLink}
                        className={styles.productName}
                    >
                        <Heading h5 bold>
                            {product.manufacturer} {product.name}
                        </Heading>
                    </CurrentStoreLink>
                </div>
            </VStack>
        );
    }
);

const ProductTableView = memo(
    ({
        product,
        loading = false,
        renderInstantly = false,
        ...ariaProps
    }: ProductTableViewProps) => {
        const {
            isShow,
            ref,
            activeSKU,
            productDetailsLink,
            customerPrice,
            startingPrice,
        } = useProductView({
            product,
            renderInstantly,
        });

        return (
            <div
                className={clsx(styles.productTableView, {
                    [styles.disableInteractions]: loading,
                })}
                ref={ref}
                {...ariaProps}
            >
                {isShow && (
                    <div className={clsx(styles.content)}>
                        <div className={clsx(styles.productBlock)}>
                            <ProductImageContent
                                product={product}
                                productDetailsLink={productDetailsLink}
                            />
                            <ProductDetails
                                product={product}
                                productDetailsLink={productDetailsLink}
                                activeSKU={activeSKU}
                            />
                            <div className={styles.availabilityBlock}>
                                <ProductTileAvailability
                                    persistMessage
                                    vertical
                                    skuList={product.variants.map(
                                        item => item.sku
                                    )}
                                    activeSKU={activeSKU}
                                />
                            </div>
                            <div></div>
                        </div>

                        {customerPrice && customerPrice.retailPrice > 0 ? (
                            <div className={styles.actionBlock}>
                                <HStack
                                    gap="small"
                                    align="center"
                                    justify="spaceBetween"
                                    expanded
                                >
                                    <ProductTablePrice
                                        product={product}
                                        selectedSKU={activeSKU}
                                        customerPrice={customerPrice}
                                        variantStartingPrice={startingPrice}
                                    />
                                    <ProductTileActions
                                        activeSKU={activeSKU}
                                        noMargin
                                    />
                                </HStack>
                            </div>
                        ) : (
                            <div className={styles.alert}>
                                <ErrorAlertBanner noBorder noPadding>
                                    <Text secondary>
                                        <FormattedMessage id="Price loading error" />
                                    </Text>
                                </ErrorAlertBanner>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

export default memo(
    withErrorBoundary(ProductTableView, {
        customMessage: <FormattedMessage id="Product can't be displayed" />,
    })
);
